import React, { useState, useMemo, useRef, useEffect } from "react";
import airports from "../data/airports.json";
import COUNTRIES from "../data/countries_with_codes";

const CIVILITIES = [
  { value: "MR", label: "Monsieur" },
  { value: "MRS", label: "Madame" },
  { value: "MISS", label: "Mademoiselle" },
];

const DOCUMENT_TYPES = [
  { value: "PASSPORT", label: "Passeport" },
  { value: "ID", label: "Carte d'identité" },
];

const TYPE_LABELS = {
  ADULT: "Adulte",
  CHILD: "Enfant",
  HELD_INFANT: "Bébé",
};

// Liste des pays de la CEDEAO (codes ISO)
const CEDEAO_COUNTRIES = [
  "BJ", "BF", "CV", "CI", "GM", "GH", "GN", "GW", 
  "LR", "ML", "NE", "NG", "SN", "SL", "TG"
];

// --- Composant SearchableSelect ---
function SearchableSelect({ label, value, onChange, options, placeholder, className, flag, searchPlaceholder }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  const selectedOption = options.find(o => o.value === value);

  const filteredOptions = useMemo(() => {
    if (!search) return options;
    const s = search.toLowerCase();
    return options.filter(o =>
      o.label.toLowerCase().includes(s) ||
      (o.code && o.code.toLowerCase().includes(s)) ||
      (o.phoneCode && o.phoneCode.includes(s))
    );
  }, [options, search]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearch("");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (option) => {
    onChange(option.value);
    setIsOpen(false);
    setSearch("");
  };

  return (
    <div className="relative" ref={containerRef}>
      <label className="block text-sm font-medium mb-1">{label}</label>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center gap-2 px-3 py-2 border rounded-lg bg-white text-left 
          focus:outline-none focus:ring-2 focus:ring-blue-400 ${className || ""}`}
      >
        {flag && selectedOption && (
          <img
            src={`https://flagcdn.com/w20/${selectedOption.value.toLowerCase()}.png`}
            alt=""
            className="w-5 h-3.5 rounded shadow"
          />
        )}
        {flag && selectedOption && (
          <span className="text-gray-500 font-mono text-sm">+{selectedOption.phoneCode}</span>
        )}
        <span className={`flex-1 ${!selectedOption ? "text-gray-400" : "text-gray-800"}`}>
          {selectedOption ? selectedOption.label : (placeholder || "Sélectionnez...")}
        </span>
        <svg className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-20 mt-1 w-full bg-white border rounded-lg shadow-xl max-h-64 overflow-hidden">
          <div className="p-2 border-b sticky top-0 bg-white">
            <input
              ref={searchInputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={searchPlaceholder || "Rechercher..."}
              className="w-full px-3 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
          <div className="overflow-y-auto max-h-48">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-4 text-center text-gray-500 text-sm">Aucun résultat</div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleSelect(option)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-blue-50 
                    ${option.value === value ? "bg-blue-100 font-medium" : ""}`}
                >
                  {flag && (
                    <img
                      src={`https://flagcdn.com/w20/${option.value.toLowerCase()}.png`}
                      alt=""
                      className="w-5 h-3.5 rounded shadow flex-shrink-0"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                  {option.phoneCode && (
                    <span className="text-gray-500 font-mono text-xs w-10 flex-shrink-0">+{option.phoneCode}</span>
                  )}
                  <span className="flex-1 truncate">{option.label}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Initialise passagers selon travelerPricings
function getInitialPassengers(travelerPricings) {
  const types = travelerPricings?.length
    ? travelerPricings.map((tp) => tp.travelerType)
    : ["ADULT"];
  return types.map((type) => ({
    civility: "",
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    type,
    email: "",
    phone: "",
    countryCode: "CI",
    documentNumber: "",
    documentType: "PASSPORT",
    documentExpiry: "",
    issuingCountry: "CI",
  }));
}

export default function PassengerForm({ flight, onClose, onReservation }) {
  const travelerPricings = flight?.travelerPricings || [];
  const [passengers, setPassengers] = useState(
    getInitialPassengers(travelerPricings)
  );
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);

  // Options pour les selects
  const countryOptions = useMemo(() =>
    COUNTRIES.map(c => ({ value: c.code, label: c.name, phoneCode: c.phoneCode })),
  []);

  const phoneOptions = useMemo(() =>
    COUNTRIES.map(c => ({ value: c.code, label: c.name, phoneCode: c.phoneCode })),
  []);

  // Fonction pour récupérer le pays d'un aéroport via son code IATA
  const getCountryByIata = (iataCode) => {
    const airport = airports.find((a) => a.iata_code === iataCode);
    return airport?.iso_country || null;
  };

  // Déterminer le type de vol (national / cedeao / international)
  const flightType = useMemo(() => {
    try {
      const allSegments = flight.itineraries?.flatMap((it) => it.segments) || [];
      if (allSegments.length === 0) return "international";

      const departureCountry = getCountryByIata(allSegments[0]?.departure?.iataCode);
      const arrivalCountry = getCountryByIata(allSegments[allSegments.length - 1]?.arrival?.iataCode);

      if (!departureCountry || !arrivalCountry) return "international";

      // Vol national : même pays
      if (departureCountry === arrivalCountry) {
        return "national";
      }

      // Vol intra-CEDEAO
      if (
        CEDEAO_COUNTRIES.includes(departureCountry) &&
        CEDEAO_COUNTRIES.includes(arrivalCountry)
      ) {
        return "cedeao";
      }

      // Vol international
      return "international";
    } catch (e) {
      console.error("Erreur flightType:", e);
      return "international";
    }
  }, [flight]);

  // Documents acceptés selon le type de vol
  const allowedDocuments = useMemo(() => {
    if (flightType === "national" || flightType === "cedeao") {
      return DOCUMENT_TYPES;
    }
    return [{ value: "PASSPORT", label: "Passeport" }];
  }, [flightType]);

  const itineraryType = flightType === "national" ? "domestic" : "international";

  const adjustedPrice =
    itineraryType === "international"
      ? parseInt(flight.price?.total || 0) + 50000
      : parseInt(flight.price?.total || 0);

  // Couleurs et labels pour le badge du type de vol
  const flightTypeInfo = useMemo(() => {
    switch (flightType) {
      case "national":
        return { bg: "bg-blue-100", text: "text-blue-800", label: "Vol national 🇨🇮" };
      case "cedeao":
        return { bg: "bg-green-100", text: "text-green-800", label: "Vol CEDEAO 🌍" };
      default:
        return { bg: "bg-orange-100", text: "text-orange-800", label: "Vol international ✈️" };
    }
  }, [flightType]);

  const validatePassengers = () => {
    for (const p of passengers) {
      if (
        !p.firstName ||
        !p.lastName ||
        !p.dateOfBirth ||
        !p.documentNumber ||
        !p.documentType ||
        !p.documentExpiry ||
        !p.issuingCountry
      ) {
        alert("⚠️ Merci de compléter toutes les informations obligatoires.");
        return false;
      }
      if (p.type === "ADULT" && (!p.email || !p.phone)) {
        alert("⚠️ L'adulte doit avoir un email et un numéro de téléphone.");
        return false;
      }
      if (flightType === "international" && p.documentType === "ID") {
        alert("⚠️ Pour les vols internationaux (hors CEDEAO), seul le Passeport est accepté.");
        return false;
      }
    }
    return true;
  };

  const handleChange = (idx, field, value) => {
    setPassengers((prev) =>
      prev.map((p, i) => (i === idx ? { ...p, [field]: value } : p))
    );
  };

  const handleNext = () => step < passengers.length - 1 && setStep(step + 1);
  const handlePrev = () => step > 0 && setStep(step - 1);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validatePassengers()) return;

    const travelers = passengers.map((p, idx) => ({
      id: String(idx + 1),
      travelerType: p.type,
      name: {
        firstName: p.firstName.trim().toUpperCase(),
        lastName: p.lastName.trim().toUpperCase(),
      },
      gender: p.civility === "MR" ? "MALE" : "FEMALE",
      dateOfBirth: p.dateOfBirth,
      documents: [
        {
          documentType: p.documentType,
          number: p.documentNumber,
          expiryDate: p.documentExpiry,
          issuanceCountry: p.issuingCountry,
          nationality: p.issuingCountry,
          holder: true,
        },
      ],
      ...(p.type === "ADULT" && {
        contact: {
          emailAddress: p.email,
          phones: [
            {
              deviceType: "MOBILE",
              countryCallingCode: COUNTRIES.find(c => c.code === p.countryCode)?.phoneCode || "225",
              number: p.phone,
            },
          ],
        },
      }),
    }));

    const flightWithMeta = {
      ...flight,
      adjustedPrice,
      itineraryType,
    };

    const orderPayload = {
      data: {
        type: "flight-order",
        flightOffers: [flightWithMeta],
        travelers,
        contact: {
          email: passengers[0].email,
          phone: {
            countryCallingCode: COUNTRIES.find(c => c.code === passengers[0].countryCode)?.phoneCode || "225",
            number: passengers[0].phone,
          }
        }
      },
    };

    setLoading(true);
    fetch(
      `${process.env.REACT_APP_BACKEND_URL}/backend_payment/verify_payment.php`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          orderPayload,
          phone_number: passengers[0].phone,
          email: passengers[0].email,
        }),
      }
    )
      .then((res) => res.json())
      .then((data) => {
        setLoading(false);
        if (data.status === "success") {
          alert("✅ Réservation enregistrée avec succès !");
          if (onReservation)
            onReservation({ ...data, email: passengers[0].email });
          if (onClose) onClose();
        } else {
          alert("❌ Erreur réservation : " + data.message);
        }
      })
      .catch(() => {
        setLoading(false);
        alert("⚠️ Impossible d'enregistrer la réservation.");
      });
  };

  const p = passengers[step];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60"
      style={{ backdropFilter: "blur(5px)" }}
    >
      {loading && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-70 z-50">
          <div className="w-16 h-16 border-4 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-white mt-4 text-lg font-semibold animate-pulse">
            Enregistrement de la réservation...
          </p>
        </div>
      )}

      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg flex flex-col h-[90vh]">
        <button
          onClick={onClose}
          className="absolute top-3 right-4 text-gray-500 hover:text-gray-800 text-2xl z-10"
          aria-label="Fermer"
        >
          ✕
        </button>

        <div className="p-6 border-b rounded-t-2xl bg-gradient-to-r from-blue-50 to-blue-100">
          <h2 className="text-xl font-bold flex items-center justify-between">
            <span className="text-blue-700">
              {flight.itineraries?.[0]?.segments?.[0]?.departure?.iataCode || "???"} →{" "}
              {flight.itineraries?.[0]?.segments?.at(-1)?.arrival?.iataCode || "???"}
            </span>
            <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm">
              Étape {step + 1}/{passengers.length}
            </span>
          </h2>
          <p className="text-gray-600 text-sm mt-1">
            Renseignez les informations du passager{" "}
            <span className="font-medium text-blue-600">
              ({TYPE_LABELS[p.type]})
            </span>
          </p>
          <div className={`mt-2 px-3 py-1.5 rounded-lg text-xs font-medium inline-block ${flightTypeInfo.bg} ${flightTypeInfo.text}`}>
            {flightTypeInfo.label}
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto px-6 py-4 space-y-6"
        >
          <div className="border rounded-lg p-4 bg-gray-50 shadow-sm space-y-4">
            {/* Civilité */}
            <div>
              <label className="block text-sm font-medium mb-1">Civilité</label>
              <select
                value={p.civility}
                onChange={(e) => handleChange(step, "civility", e.target.value)}
                className="border p-2 rounded w-full bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
                required
              >
                <option value="">Sélectionnez</option>
                {CIVILITIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>

            {/* Prénom */}
            <div>
              <label className="block text-sm font-medium mb-1">Prénom</label>
              <input
                type="text"
                placeholder="Prénom"
                value={p.firstName}
                onChange={(e) => handleChange(step, "firstName", e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                required
              />
            </div>

            {/* Nom */}
            <div>
              <label className="block text-sm font-medium mb-1">Nom</label>
              <input
                type="text"
                placeholder="Nom"
                value={p.lastName}
                onChange={(e) => handleChange(step, "lastName", e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                required
              />
            </div>

            {/* Date naissance */}
            <div>
              <label className="block text-sm font-medium mb-1">Date de naissance</label>
              <input
                type="date"
                value={p.dateOfBirth}
                onChange={(e) => handleChange(step, "dateOfBirth", e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                required
              />
            </div>

            {/* Infos adulte */}
            {p.type === "ADULT" && (
              <>
                <div>
                  <label className="block text-sm font-medium mb-1">Email</label>
                  <input
                    type="email"
                    placeholder="exemple@email.com"
                    value={p.email}
                    onChange={(e) => handleChange(step, "email", e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                    required
                  />
                </div>

                {/* Téléphone avec sélecteur d'indicatif moderne */}
                <div>
                  <label className="block text-sm font-medium mb-1">Téléphone</label>
                  <div className="flex gap-2">
                    <div className="w-2/5">
                      <SearchableSelect
                        value={p.countryCode}
                        onChange={(v) => handleChange(step, "countryCode", v)}
                        options={phoneOptions}
                        placeholder="Indicatif"
                        flag={true}
                        searchPlaceholder="Rechercher un pays..."
                      />
                    </div>
                    <div className="flex-1">
                      <input
                        type="tel"
                        placeholder="Numéro de téléphone"
                        value={p.phone}
                        onChange={(e) => handleChange(step, "phone", e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                        required
                      />
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Message d'information sur le type de vol */}
            <div className={`p-3 rounded-lg border-l-4 ${
              flightType === "national" 
                ? "bg-blue-50 border-blue-500" 
                : flightType === "cedeao"
                ? "bg-green-50 border-green-500"
                : "bg-orange-50 border-orange-500"
            }`}>
              <p className="text-sm font-medium">
                {flightType === "national" && (
                  <>
                    <span className="text-blue-700">Vol national 🇨🇮</span>
                    <span className="block text-gray-600 mt-1">
                      Carte d'identité ou Passeport acceptés
                    </span>
                  </>
                )}
                {flightType === "cedeao" && (
                  <>
                    <span className="text-green-700">Vol intra-CEDEAO 🌍</span>
                    <span className="block text-gray-600 mt-1">
                      Carte d'identité ou Passeport acceptés
                    </span>
                  </>
                )}
                {flightType === "international" && (
                  <>
                    <span className="text-orange-700">Vol international ✈️</span>
                    <span className="block text-gray-600 mt-1">
                      Passeport obligatoire uniquement
                    </span>
                  </>
                )}
              </p>
            </div>

            {/* Type de document */}
            <div>
              <label className="block text-sm font-medium mb-1">Type de document</label>
              <select
                value={p.documentType}
                onChange={(e) => handleChange(step, "documentType", e.target.value)}
                className="w-full px-3 py-2 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
                required
              >
                <option value="">Sélectionnez</option>
                {allowedDocuments.map((d) => (
                  <option key={d.value} value={d.value}>{d.label}</option>
                ))}
              </select>
            </div>

            {/* Numéro de document */}
            <div>
              <label className="block text-sm font-medium mb-1">Numéro de document</label>
              <input
                type="text"
                placeholder="Numéro du document"
                value={p.documentNumber}
                onChange={(e) => handleChange(step, "documentNumber", e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                required
              />
            </div>

            {/* Date d'expiration */}
            <div>
              <label className="block text-sm font-medium mb-1">Date d'expiration</label>
              <input
                type="date"
                value={p.documentExpiry}
                onChange={(e) => handleChange(step, "documentExpiry", e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                required
              />
            </div>

            {/* Nationalité (pays émetteur) - SearchableSelect avec drapeaux */}
            <div>
              <SearchableSelect
                label="Nationalité (pays émetteur)"
                value={p.issuingCountry}
                onChange={(v) => handleChange(step, "issuingCountry", v)}
                options={countryOptions}
                placeholder="Sélectionnez un pays..."
                flag={true}
                searchPlaceholder="Rechercher un pays..."
              />
            </div>
          </div>

          {/* Navigation */}
          <div className="flex justify-between gap-3 sticky bottom-0 bg-white pt-3 pb-2">
            <button
              type="button"
              onClick={onClose}
              className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded-lg"
            >
              Annuler
            </button>
            <div className="flex gap-2">
              {step > 0 && (
                <button
                  type="button"
                  onClick={handlePrev}
                  className="bg-blue-100 hover:bg-blue-200 text-blue-700 px-4 py-2 rounded-lg font-semibold"
                >
                  Précédent
                </button>
              )}
              {step < passengers.length - 1 ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-semibold"
                >
                  Suivant
                </button>
              ) : (
                <button
                  type="submit"
                  className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-semibold"
                >
                  Confirmer
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}